import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";
import { toast } from "sonner";

const roleColor = { admin: "text-blue-300", owner: "text-amber-300", sales: "text-cyan-300" };

function renderWithMentions(text, directory) {
  if (!directory.length) return text;
  const names = [...directory].sort((a, b) => b.name.length - a.name.length).map((u) => u.name);
  const pattern = new RegExp(`@(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`, "gi");
  const parts = [];
  let last = 0, m;
  while ((m = pattern.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<span key={m.index} className="text-blue-300 font-medium">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return parts;
}

export default function ChatBox({ channel = "team", height = "60vh", readOnly = false }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const boxRef = useRef();
  const inputRef = useRef();
  const { data: msgs = [] } = useQuery({
    queryKey: ["chat", channel],
    queryFn: async () => (await api.get(`/chat?channel=${encodeURIComponent(channel)}`)).data,
    refetchInterval: 3000,
  });
  const { data: directory = [] } = useQuery({ queryKey: ["users-directory"], queryFn: async () => (await api.get("/users/directory")).data });
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs]);

  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const pos = e.target.selectionStart ?? val.length;
    const m = val.slice(0, pos).match(/@([^\s@]*)$/);
    setMentionQuery(m ? m[1] : null);
  };

  const selectMention = (u) => {
    const el = inputRef.current;
    const pos = el?.selectionStart ?? text.length;
    const before = text.slice(0, pos).replace(/@([^\s@]*)$/, `@${u.name} `);
    const after = text.slice(pos);
    setText(before + after);
    setMentionQuery(null);
    requestAnimationFrame(() => el?.focus());
  };

  const filteredDirectory = mentionQuery !== null
    ? directory.filter((u) => u.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText(""); setMentionQuery(null);
    // Optimistic append — the message shows up the instant you hit send, not after a
    // full round-trip + refetch, the same feel WhatsApp/Telegram have. The temp id gets
    // wiped out (and replaced by the real one) as soon as the refetch below lands.
    const tempId = `optimistic-${Date.now()}`;
    qc.setQueryData(["chat", channel], (old = []) => [...old, {
      msg_id: tempId, channel, user_id: user?.user_id, name: user?.name, role: user?.role,
      text: t, created_at: new Date().toISOString(),
    }]);
    try {
      await api.post("/chat", { channel, text: t });
      qc.invalidateQueries({ queryKey: ["chat", channel] });
    } catch (e) {
      toast.error(apiErr(e)); setText(t);
      qc.setQueryData(["chat", channel], (old = []) => old.filter((m) => m.msg_id !== tempId));
    }
  };
  return (
    <div className="flex flex-col" style={{ height }} data-testid="chatbox">
      <div ref={boxRef} className="flex-1 overflow-y-auto hide-scrollbar space-y-2 p-1">
        {msgs.length === 0 && <div className="text-center text-zinc-600 text-sm py-8">Belum ada pesan. Mulai diskusi! Ketik @ untuk menandai seseorang.</div>}
        {msgs.map((m) => {
          const mine = m.user_id === user?.user_id;
          return (
            <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-blue-600 text-white" : "bg-[#18181B]"}`}>
                {!mine && <div className={`text-[11px] font-semibold ${roleColor[m.role] || "text-zinc-400"}`}>{m.name} · {m.role}</div>}
                <div className="text-sm whitespace-pre-wrap break-words">{renderWithMentions(m.text, directory)}</div>
                <div className="text-[10px] opacity-60 mt-0.5 text-right">{fmtDateTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {readOnly ? (
        <div className="text-center text-xs text-zinc-600 pt-2 border-t border-white/5">Grup ini hanya-baca untuk Anda</div>
      ) : (
      <div className="relative flex gap-2 pt-2">
        {mentionQuery !== null && filteredDirectory.length > 0 && (
          <div className="absolute bottom-full left-0 mb-1 w-64 card-surface rounded-xl border border-white/10 shadow-xl overflow-hidden">
            {filteredDirectory.map((u) => (
              <button key={u.user_id} onClick={() => selectMention(u)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between">
                <span>{u.name}</span>
                <span className="text-[10px] text-zinc-500 capitalize">{u.role}</span>
              </button>
            ))}
          </div>
        )}
        <Input ref={inputRef} data-testid="chat-input" value={text} onChange={onTextChange}
          onKeyDown={(e) => { if (e.key === "Enter" && mentionQuery === null) send(); if (e.key === "Escape") setMentionQuery(null); }}
          onFocus={() => setTimeout(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, 250)}
          placeholder="Tulis pesan… ketik @ untuk tag" className="bg-[#18181B] border-white/10 h-10" />
        <Button data-testid="chat-send" onClick={send} className="h-10 bg-blue-600 hover:bg-blue-700 px-3"><Send size={16} /></Button>
      </div>
      )}
    </div>
  );
}
